<template>
  <div class="reward-spend-section">
    <h5 class="reward-spend-section__title">Reward Spend</h5>
    <b-row class="reward-spend-section__row">
      <b-col cols="12" lg="6">
        <div class="reward-spend-section__stats-grid">
          <app-stat :stat="summary?.total_granted_minor" :formatter="toCurrency" description="Total granted" />
          <app-stat :stat="summary?.total_redeemed_minor" :formatter="toCurrency" description="Total redeemed" />
          <app-stat :stat="summary?.awards_granted" description="Awards granted" />
          <app-stat :stat="summary?.user_participation" suffix="%">
            <p class="reward-spend-section__stat-description">
              User participation
              <span id="participation-info" class="reward-spend-section__info-icon">
                <font-awesome-icon :icon="['fal', 'info-circle']" />
              </span>
            </p>
            <b-tooltip target="participation-info" placement="top" variant="light">
              Share of eligible employees who've given or received recognition this period
            </b-tooltip>
          </app-stat>
        </div>
      </b-col>
      <b-col cols="12" lg="6">
        <div class="reward-spend-section__chart-card">
          <div class="reward-spend-section__chart-header">
            <h6 class="mb-0">Spend over time</h6>
            <b-form-select
              v-model="spendMode"
              :options="RECOGNITION_ANALYTICS_SPEND_MODES"
              class="reward-spend-section__spend-toggle"
            />
          </div>
          <bar-chart
            v-if="chartSeriesX.values.length > 0"
            :series-x="chartSeriesX"
            :series-y="chartSeriesY"
            :chart-options="chartOptions"
            :chart-dataset-options="chartDatasetOptions"
          />
          <div v-else-if="!loading" class="reward-spend-section__chart-empty">
            <p class="text-muted mb-0">No spend data available for the selected period.</p>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import AppStat from '@/components/AppStat.vue';
import BarChart, { CHART_PRESET_OPTIONS } from '@/components/charts/BarChart.vue';
import PaletteColors from '@/styles/palette-colors.module.scss';
import { RECOGNITION_ANALYTICS_SPEND_MODES } from '@/pages/recognition/constants';
import { getDatasetColor } from '@/utils/chart';
import { toCurrency } from '@/utils';
import merge from 'lodash/merge';

const props = defineProps({
  summary: {
    type: Object,
    default: undefined,
  },
  spendOverTime: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const spendMode = ref('granted');

const chartSeriesX = computed(() => ({
  values: (props.spendOverTime?.labels || []).map(label => ({ name: label })),
}));

const chartSeriesY = computed(() => {
  if (!props.spendOverTime?.datasets) {
    return [];
  }
  const key = spendMode.value === 'granted' ? 'granted_values_minor' : 'redeemed_values_minor';
  return props.spendOverTime.datasets.map((dataset, index) => ({
    name: dataset.division_name,
    values: (dataset[key] || []).map(v => v / 100),
    color: PaletteColors[getDatasetColor(index)],
  }));
});

const chartOptions = computed(() =>
  merge({}, CHART_PRESET_OPTIONS.CURRENCY, {
    scales: {
      x: { stacked: true },
      y: { stacked: true },
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
        },
      },
    },
  }),
);

const chartDatasetOptions = { borderRadius: 4, maxBarThickness: 40 };
</script>

<style scoped lang="scss">
.reward-spend-section {
  margin-bottom: 1.5rem;

  &__title {
    font-weight: 600;
    margin-bottom: 1rem;
    color: var(--palette-color-default);
  }

  &__row {
    --bs-gutter-x: 1rem;
  }

  &__stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;

    @media (min-width: 992px) {
      height: 100%;
      margin-bottom: 0;
    }
  }

  &__chart-card {
    background-color: var(--palette-color-base-white);
    border-radius: 4px;
    box-shadow: var(--depth-2);
    padding: 1.25rem 1.5rem;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  &__chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  &__spend-toggle {
    width: auto;
    min-width: 120px;
    border-color: var(--palette-color-default-lighten-80);
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
  }

  &__chart-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 200px;
  }

  &__stat-description {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
    margin: 0;
    color: var(--palette-color-default-lighten-30);
  }

  &__info-icon {
    cursor: pointer;
    color: var(--palette-color-default-lighten-40);
    font-size: 0.875rem;
    margin-left: 0.25rem;
  }

  :deep(.app-stat__value) {
    color: var(--palette-color-default);
  }

  :deep(.app-stat__description) {
    color: var(--palette-color-default-lighten-30);
  }

  :deep(.app-stat__subtitle) {
    font-size: 20px;
    font-weight: 600;
    color: var(--palette-color-default-lighten-30);
  }
}
</style>
